import React from 'react';
import { 
  Box, 
  Grid, 
  Link, 
  Typography, 
  Button, 
  Container, 
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FacebookIcon from './cib-facebook.svg';
import DiscordIcon from './cib-discord.svg';
import InstagramIcon from './cib-instagram.svg';
import MessengerIcon from './cib-messenger.svg';
import SupportButton from '../Buttons/supportButton';

// Icons
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CopyrightIcon from '@mui/icons-material/Copyright';
import AdSense from '../googleAds';
import { trackOutboundClick } from '../../utils/analytics';

const DISCORD_URL = 'https://discord.gg/PxCCnnc529';

// Custom styled components
const FooterRoot = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: '#1a1a1a',
  color: 'white',
  padding: '40px 0 20px',
  position: 'relative',
  zIndex: 10,
  marginTop: 'auto',
}));

const SocialIcon = styled('img')({
  width: 30,
  height: 30,
  filter: 'brightness(0) invert(1)',
  transition: 'transform 0.3s ease, opacity 0.2s ease',
  '&:hover': {
    transform: 'scale(1.15) translateY(-3px)',
    opacity: 0.85,
  },
});

const FooterLink = styled(Link)(({ theme }) => ({
  color: '#f5f5f5',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  margin: '6px 0',
  transition: 'color 0.2s ease, transform 0.2s ease',
  '&:hover': {
    color: '#ff9800',
    transform: 'translateX(3px)',
    textDecoration: 'none',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  marginBottom: '16px',
  position: 'relative',
  paddingBottom: '8px',
  '&:after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 0,
    height: '2px',
    width: '40px',
    backgroundColor: '#ff9800',
  },
}));

const ContactButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ff9800',
  color: 'white',
  padding: '10px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s',
  marginTop: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  '&:hover': {
    backgroundColor: '#e68900',
    transform: 'translateY(-3px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
}));

function Footer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentYear = new Date().getFullYear();

  // Footer navigation links
  const footerLinks = [
    { name: 'Home', path: '/', icon: <HomeOutlinedIcon fontSize="small" /> },
    { name: 'Barn Size Calculator', path: '/barnSizeCalculator', icon: <CalculateOutlinedIcon fontSize="small" /> },
    { name: 'Find Friends', path: '/addFriendsPage', icon: <PeopleOutlineIcon fontSize="small" /> },
    { name: 'Goods List', path: '/goodsList', icon: <HelpOutlineIcon fontSize="small" /> },
    { name: 'Contact Us', path: '/contactUs', icon: <MailOutlineIcon fontSize="small" /> },
  ];

  return (
    <FooterRoot>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* About Section */}
          <Grid item xs={12} md={4}>
            <SectionTitle variant="h6">About Hay Day Calculator</SectionTitle>
            <Typography variant="body2" sx={{ 
              color: '#bdbdbd', 
              mb: 2, 
              lineHeight: 1.6,
              maxWidth: '90%'
            }}>
              A collection of useful tools and resources for Hay Day players. Created with passion to help
              farmers optimize their gameplay, connect with others, and enjoy the farming experience.
            </Typography>
            <AdSense />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
              <SupportButton 
                variant="default" 
                animate={true}
                customText="Support Us" 
              />
            </Box>
          </Grid>

          {/* Quick Links Section */}
          <Grid item xs={12} sm={6} md={3}>
            <SectionTitle variant="h6">Quick Links</SectionTitle>
            <Box>
              {footerLinks.map((link, index) => (
                <FooterLink href={link.path} key={index}>
                  {link.icon}
                  <Typography variant="body2">{link.name}</Typography>
                </FooterLink>
              ))}
            </Box>
          </Grid>

          {/* Connect Section */}
          <Grid item xs={12} sm={6} md={2}>
            <SectionTitle variant="h6">Connect</SectionTitle>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              <Grid item>
                <IconButton component={Link} href="https://www.facebook.com/maskararaghav/" target="_blank" rel="noopener" aria-label="Facebook">
                  <SocialIcon src={FacebookIcon} alt="Facebook" />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton component={Link} href="https://www.instagram.com/raghavmaskara/" target="_blank" rel="noopener" aria-label="Instagram">
                  <SocialIcon src={InstagramIcon} alt="Instagram" />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton component={Link} href="https://m.me/maskararaghav" target="_blank" rel="noopener" aria-label="Messenger">
                  <SocialIcon src={MessengerIcon} alt="Messenger" />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton component={Link} href={DISCORD_URL} target="_blank" rel="noopener" aria-label="Discord" onClick={() => trackOutboundClick('discord', DISCORD_URL)}>
                  <SocialIcon src={DiscordIcon} alt="Discord" />
                </IconButton>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ color: '#e0e0e0', mb: 1, fontWeight: 500 }}>
                Need help?
              </Typography>
              <ContactButton href="/contactUs" size="small" startIcon={<MailOutlineIcon />}>
                Contact Us
              </ContactButton>
            </Box>
          </Grid>

          {/* Community Section */}
          <Grid item xs={12} md={3}>
            <SectionTitle variant="h6">Join Our Community</SectionTitle>
            <Typography variant="body2" sx={{ color: '#bdbdbd', mb: 2 }}>
              Share tips, get help, and connect with other Hay Day players.
            </Typography>
            <Button 
              variant="outlined" 
              href={DISCORD_URL}
              target="_blank"
              rel="noopener"
              onClick={() => trackOutboundClick('discord', DISCORD_URL)}
              startIcon={<img src={DiscordIcon} alt="" style={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }} />}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.8)',
                  backgroundColor: 'rgba(255,255,255,0.05)'
                }
              }}
            >
              Join Discord Server
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 4, mb: 3, backgroundColor: 'rgba(255,255,255,0.1)' }} />
        
        {/* Bottom Credits */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'center' : 'flex-start',
          textAlign: isMobile ? 'center' : 'left',
          gap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CopyrightIcon fontSize="small" sx={{ opacity: 0.7, fontSize: '0.9rem' }} />
            <Typography variant="body2" sx={{ color: '#9e9e9e', fontSize: '0.85rem' }}>
              {currentYear} | Created with <FavoriteBorderIcon sx={{ fontSize: '0.9rem', color: '#f44336', mx: 0.2, verticalAlign: 'middle' }} /> by <strong>Raghav Maskara</strong>
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Link href="/home" sx={{ color: '#9e9e9e', fontSize: '0.85rem', '&:hover': { color: '#ff9800' } }}>
              Privacy Policy
            </Link>
            <Link href="/home" sx={{ color: '#9e9e9e', fontSize: '0.85rem', '&:hover': { color: '#ff9800' } }}>
              Terms of Use
            </Link>
          </Box>
        </Box>
      </Container>
    </FooterRoot>
  );
}

export default Footer;